import React, { useCallback, useMemo, useState } from "react";
import { FormattedMessage, injectIntl, IntlShape } from "react-intl";
import { SortableHandle } from "react-sortable-hoc";
import { Link } from "react-router-dom";
import { useHistory } from "react-router";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faEye, faPencilAlt } from "@fortawesome/free-solid-svg-icons";
import { Table } from "@devexpress/dx-react-grid-bootstrap4";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { getSchema } from "./schema";
import getTableColumns from "./tableColumns";
import { TableColumns, TableFilters } from "../shared/crud/list";
import { SECTION } from "../../lib/constants";
import { useGrpcClient, usePropertyGrpcCall } from "../../lib/grpc";
import { ExtraAPIPromiseClient } from "../../gen/witbooking/backoffice/extra/v1/extra_api_grpc_web_pb";
import { messages } from "./messages";
import SimpleCrud from "../shared/crud/simple-crud";
import { DefaultTableRowActionsProps, Delete } from "../shared/actions/tableActions";
import { ImageManagerProvider } from "../shared/widgets/ImageManager";
import getTableFilters from "./tableFilters";
import { useChainData } from "../../components/chain-data-provider";
import { createExcelFileDownloadFromRawData } from "../../lib/supportFunctions";
import { mapToObjectFromGRPC } from "./EditExtra/mappers";
import { CustomExtraListItem, ExtraRateType } from "./types";
import { usePropertyLocales } from "../../components/property-locale-provider";
import { StyledSwapArrow } from "./styles";
import {
  DeleteExtraRequest,
  DownloadAppliedToExcelRequest,
  GetAllExtrasRequest,
  ReorderExtraRequest,
} from "../../gen/witbooking/backoffice/extra/v1/extra_api_pb";

function ExtrasCrud({ propertyTicker, intl }: { propertyTicker: string; intl: IntlShape }) {
  const history = useHistory();
  const allowedLocales = usePropertyLocales();
  const chainData = useChainData();

  const [reload, setReload] = useState(true);
  const [forceUpdate, setForceUpdate] = useState(0);

  // GRPC
  const grpcClient = useGrpcClient(ExtraAPIPromiseClient);
  const getAllExtras = usePropertyGrpcCall(grpcClient, grpcClient.getAllExtras);
  const deleteExtra = usePropertyGrpcCall(grpcClient, grpcClient.deleteExtra);
  const reorderExtra = usePropertyGrpcCall(grpcClient, grpcClient.reorderExtra);
  const downloadExcel = usePropertyGrpcCall(grpcClient, grpcClient.downloadAppliedToExcel);

  const isChain = useMemo(() => chainData?.isChain, [chainData]);

  // Schema
  const customSchema = useMemo(() => getSchema(), []);
  // Table Columns
  const tableColumns = useMemo<TableColumns<CustomExtraListItem>>(
    () => getTableColumns(intl), [intl]
  );
  // Table Filters
  const tableFilters = useMemo<TableFilters<CustomExtraListItem, CustomExtraListItem>>(
    () => getTableFilters(intl), [intl]
  );

  // List height
  const listHeight = useMemo(() => window.innerHeight - (window.innerHeight / 4), []);

  const handleViewAction = useCallback((extraId: string) => {
    history.push(`/${propertyTicker}/extras-edit/${extraId}`);
  }, [history, propertyTicker]);

  const handleDuplicateAction = useCallback((extraId: string) => {
    history.push(`/${propertyTicker}/extras-copy/${extraId}`);
  }, [history, propertyTicker]);

  // CRUD Methods
  const handleGetAll = useCallback(async (): Promise<CustomExtraListItem[]> => {
    const request = new GetAllExtrasRequest();
    const response = await getAllExtras(request);
    return response.getExtrasList().map(extra => {
      const extraObject = mapToObjectFromGRPC(extra.toObject(), allowedLocales)
      return {
        ...extraObject,
        isShared: extraObject.ticker.endsWith("_agr"),
        rate: extraObject.rateType === ExtraRateType.EXTRA_RATE_TYPE_CONSTANT
          ? extraObject.ratePrice
          : ExtraRateType.EXTRA_RATE_TYPE_OWN,
      }
    });
  }, [allowedLocales, getAllExtras]);

  const handleDelete = useCallback(async (extraId: string) => {
    const request = new DeleteExtraRequest();
    request.setId(extraId);
    await deleteExtra(request);
    setForceUpdate(forceUpdate + 1);
  }, [deleteExtra, forceUpdate]);

  const handleReOrder = useCallback(async (id: string, after: string) => {
    const request = new ReorderExtraRequest();
    request.setBeforeId(id);
    request.setAfterId(after);
    await reorderExtra(request);
    setForceUpdate(forceUpdate + 1);
  }, [reorderExtra, forceUpdate]);

  const handleExport = useCallback(async () => {
    const request = new DownloadAppliedToExcelRequest();
    const response = await downloadExcel(request);
    const fileData = response.getFileData_asU8();
    createExcelFileDownloadFromRawData(fileData, `extras-${propertyTicker}-${Date.now()}.xlsx`);
  }, [downloadExcel, propertyTicker]);

  const TableRowActions = useCallback(
    ({ row, rest }: DefaultTableRowActionsProps<any>) => {
      const DragButton = SortableHandle(() => (
        <OverlayTrigger
          delay={{show: 250, hide: 0}}
          overlay={(props) => (
            <Tooltip id={`drag-tooltip-${row.id}`} {...props}>
              <FormattedMessage {...messages.drag } />
            </Tooltip>
          )}>
            <Button
              size="sm"
              className="mr-2"
              variant="light"
              style={{ cursor: "move" }}>
              <StyledSwapArrow />
            </Button>
        </OverlayTrigger>
      ));

      return (
        <Table.Cell {...rest}>
          {!isChain && <DragButton />}

          <OverlayTrigger
            delay={{show: 250, hide: 0}}
            overlay={(props) => (
              <Tooltip id={`duplicate-tooltip-${row.id}`} {...props}>
                <FormattedMessage {...messages.duplicate } />
              </Tooltip>
            )}>
            <Button
              size="sm"
              className="mr-2"
              variant="light"
              onClick={() => handleDuplicateAction(row?.id)}>
              <FontAwesomeIcon icon={faCopy} />
            </Button>
          </OverlayTrigger>

          <OverlayTrigger
            delay={{show: 250, hide: 0}}
            overlay={(props) => (
              <Tooltip id={`edit-tooltip-${row.id}`} {...props}>
                <FormattedMessage {...row.isShared ? messages.view : messages.edit } />
              </Tooltip>
            )}>
            <Button
              size="sm"
              className="mr-2"
              variant="light"
              onClick={() => handleViewAction(row?.id)}>
              <FontAwesomeIcon icon={row.isShared ? faEye : faPencilAlt} />
            </Button>
          </OverlayTrigger>

          {!row.isShared && (
            <Delete
              rest={rest}
              onDelete={handleDelete}
              row={row}
              onReloadList={() => setReload(reload)}
              useOverlay={true}
            />
          )}
        </Table.Cell>
      );
    },
    [isChain, handleDelete, handleDuplicateAction, handleViewAction, reload]
  );

  const actions = () => {
    return (
      <Link className="btn btn-primary" to="extras-new">
        <FormattedMessage {...messages.create} />
      </Link>
    );
  };

  return (
    <ImageManagerProvider>
      <SimpleCrud<CustomExtraListItem>
        managerName={"extras"}
        fixedActions={true}
        actions={actions}
        listHeight={listHeight}
        formConfiguration={{}}
        schema={customSchema}
        tableColumns={tableColumns}
        section={SECTION.EXTRAS}
        onGetAll={handleGetAll}
        onDelete={handleDelete}
        onReorder={handleReOrder}
        messages={messages}
        rowActions={props => <TableRowActions {...props} />}
        forceLoad={forceUpdate}
        tableFilters={tableFilters}
        hideExportButton={!isChain}
        onExport={handleExport}
        onExportText={intl.formatMessage(messages.downloadExcel)}
      />
    </ImageManagerProvider>
  );
}

export default injectIntl(withAuthorizationVerifier(ExtrasCrud));
